import * as React from "react";
import { cn } from "@/lib/utils";

interface TabsProps {
	defaultValue?: string;
	value?: string;
	onValueChange?: (value: string) => void;
	children: React.ReactNode;
	className?: string;
}

interface TabsListProps {
	children: React.ReactNode;
	className?: string;
}

interface TabsTriggerProps {
	value: string;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
}

interface TabsContentProps {
	value: string;
	children: React.ReactNode;
	className?: string;
}

const TabsContext = React.createContext<{
	value: string;
	onValueChange: (value: string) => void;
} | null>(null);

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
	(
		{ defaultValue, value, onValueChange, children, className, ...props },
		ref,
	) => {
		const [internalValue, setInternalValue] = React.useState(
			defaultValue || "",
		);
		const controlledValue = value !== undefined ? value : internalValue;
		const handleValueChange = onValueChange || setInternalValue;

		return (
			<TabsContext.Provider
				value={{ value: controlledValue, onValueChange: handleValueChange }}
			>
				<div ref={ref} className={cn("w-full", className)} {...props}>
					{children}
				</div>
			</TabsContext.Provider>
		);
	},
);
Tabs.displayName = "Tabs";

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
	({ children, className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);
TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
	({ value, children, className, disabled, ...props }, ref) => {
		const context = React.useContext(TabsContext);
		if (!context) {
			throw new Error("TabsTrigger must be used within a Tabs component");
		}

		const isActive = context.value === value;

		return (
			<button
				ref={ref}
				className={cn(
					"inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
					isActive
						? "bg-background text-foreground shadow-sm"
						: "hover:bg-background/50 hover:text-foreground",
					className,
				)}
				onClick={() => !disabled && context.onValueChange(value)}
				disabled={disabled}
				{...props}
			>
				{children}
			</button>
		);
	},
);
TabsTrigger.displayName = "TabsTrigger";

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
	({ value, children, className, ...props }, ref) => {
		const context = React.useContext(TabsContext);
		if (!context) {
			throw new Error("TabsContent must be used within a Tabs component");
		}

		if (context.value !== value) {
			return null;
		}

		return (
			<div
				ref={ref}
				className={cn(
					"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);
TabsContent.displayName = "TabsContent";

export { Tabs, TabsList, TabsTrigger, TabsContent };
